import React, { useState } from 'react';
import { 
  Download, Upload, RefreshCw, AlertTriangle, Check, 
  X, FileText, Loader, Cpu, Zap, DollarSign 
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { importMinerstatToSupabase } from '../../utils/api/minerstat';
import { supabase } from '../../utils/supabase';
import { motion, AnimatePresence } from 'framer-motion';

export function MinerDataManager() {
  const [isImporting, setIsImporting] = useState(false);
  const [importStats, setImportStats] = useState<{
    inserted: number;
    updated: number;
    skipped: number;
  } | null>(null);
  const [lastImport, setLastImport] = useState<Date | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleMinerstatImport = async () => {
    try {
      setIsImporting(true);
      setImportStats(null);
      setValidationErrors([]);

      const result = await importMinerstatToSupabase();
      
      setImportStats(result);
      setLastImport(new Date());
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error('Import failed: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const validateMinerData = (data: any[]) => {
    const errors: string[] = [];
    
    data.forEach((miner, index) => {
      if (!miner.model) errors.push(`Row ${index + 1}: Missing model`);
      if (!miner.name) errors.push(`Row ${index + 1}: Missing name`);
      if (!miner.algorithm) errors.push(`Row ${index + 1}: Missing algorithm`);
      if (!miner.hashrate) errors.push(`Row ${index + 1}: Missing hashrate`);
      if (!miner.power) errors.push(`Row ${index + 1}: Missing power`);
      if (!miner.manufacturer) errors.push(`Row ${index + 1}: Missing manufacturer`);
      
      // Validate algorithm
      const validAlgorithms = ['SHA-256', 'Scrypt', 'Ethash', 'Blake2s'];
      if (!validAlgorithms.includes(miner.algorithm)) {
        errors.push(`Row ${index + 1}: Invalid algorithm "${miner.algorithm}"`);
      }
      
      // Validate hashrate unit
      const validUnits = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s', 'EH/s'];
      if (!validUnits.includes(miner.hashrateUnit)) {
        errors.push(`Row ${index + 1}: Invalid hashrate unit "${miner.hashrateUnit}"`);
      }
      
      // Validate release year
      const year = parseInt(miner.releaseYear);
      if (isNaN(year) || year < 2009 || year > new Date().getFullYear() + 1) {
        errors.push(`Row ${index + 1}: Invalid release year "${miner.releaseYear}"`);
      }
    });

    return errors;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationErrors([]);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!Array.isArray(data)) {
        setValidationErrors(['File must contain an array of miner data']);
        return;
      }

      const errors = validateMinerData(data);
      setValidationErrors(errors);

      if (errors.length === 0) {
        toast.success('File validated successfully');
      }
    } catch (error: any) {
      console.error('File validation error:', error);
      setValidationErrors(['Invalid JSON format']);
      toast.error('Failed to validate file');
    }
  };

  const handleManualImport = async () => {
    if (!selectedFile || validationErrors.length > 0) return;

    try {
      setIsImporting(true);
      const text = await selectedFile.text();
      const data = JSON.parse(text);

      const { data: result, error } = await supabase
        .rpc('import_minerstat_data', {
          minerstat_data: data
        });

      if (error) throw error;

      setImportStats(result);
      setLastImport(new Date());
      setSelectedFile(null);
      toast.success('Manual import completed successfully');
    } catch (error: any) {
      console.error('Manual import error:', error);
      toast.error('Manual import failed: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Import Controls */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Cpu className="w-5 h-5 text-blue-500" />
          Miner Data Management
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Minerstat Import */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Import from Minerstat</h3>
            <p className="text-sm text-gray-500">
              Fetch and import the latest miner data from Minerstat API.
            </p>
            <button
              onClick={handleMinerstatImport}
              disabled={isImporting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Import from Minerstat
                </>
              )}
            </button>
          </div>

          {/* Manual Import */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">Manual Import</h3>
            <p className="text-sm text-gray-500">
              Import miner data from a JSON file.
            </p>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-medium
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              {selectedFile && (
                <button
                  onClick={handleManualImport}
                  disabled={isImporting || validationErrors.length > 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isImporting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Import
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      <AnimatePresence>
        {validationErrors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-50 rounded-xl p-6 border border-red-100"
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="font-medium text-red-800">Validation Errors</h3>
            </div>
            <ul className="space-y-2">
              {validationErrors.map((error, index) => (
                <li key={index} className="text-sm text-red-600 flex items-center gap-2">
                  <X className="w-4 h-4" />
                  {error}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import Results */}
      <AnimatePresence>
        {importStats && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-50 rounded-xl p-6 border border-green-100"
          >
            <div className="flex items-center gap-2 mb-4">
              <Check className="w-5 h-5 text-green-500" />
              <h3 className="font-medium text-green-800">Import Completed</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Inserted</span>
                  <span className="text-lg font-semibold text-green-600">{importStats.inserted}</span>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Updated</span>
                  <span className="text-lg font-semibold text-blue-600">{importStats.updated}</span>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Skipped</span>
                  <span className="text-lg font-semibold text-gray-600">{importStats.skipped}</span>
                </div>
              </div>
            </div>
            {lastImport && (
              <p className="mt-4 text-sm text-gray-600">
                Last import: {lastImport.toLocaleString()}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Documentation */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-blue-500" />
          <h3 className="font-medium text-gray-900">Import Documentation</h3>
        </div>
        <div className="prose prose-sm max-w-none">
          <h4 className="text-gray-900">Manual Import Format</h4>
          <p className="text-gray-600">
            The JSON file should contain an array of miner objects with the following structure:
          </p>
          <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto">
{`[
  {
    "model": "S21 XP",
    "name": "Antminer S21 XP",
    "algorithm": "SHA-256",
    "hashrate": 335,
    "hashrateUnit": "TH/s",
    "power": 5390,
    "manufacturer": "Bitmain",
    "releaseYear": 2024,
    "status": "current",
    "specs": {
      "noise": 75,
      "cooling": "Air",
      "interface": "Ethernet"
    }
  }
]`}
          </pre>
          <h4 className="text-gray-900 mt-4">Required Fields</h4>
          <ul className="list-disc list-inside text-gray-600">
            <li>model (string): Unique identifier for the miner</li>
            <li>name (string): Display name</li>
            <li>algorithm (string): One of: SHA-256, Scrypt, Ethash, Blake2s</li>
            <li>hashrate (number): Mining hashrate</li>
            <li>hashrateUnit (string): One of: H/s, KH/s, MH/s, GH/s, TH/s, PH/s, EH/s</li>
            <li>power (number): Power consumption in watts</li>
            <li>manufacturer (string): Manufacturer name</li>
            <li>releaseYear (number): Year of release (2009-present)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}